import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Typography,
    Box,
    Card,
    Stack,
    LinearProgress,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    InputAdornment,
    Chip
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SearchIcon from '@mui/icons-material/Search';
import { fetchAllShipFromSavings, fetchDistinctStores, fetchRecentFlagged } from './shipFromSavingsSlice';
import FlaggedOrdersDrawer from './FlaggedOrdersDrawer';

// Savings-specific accent, distinct from the app's primary blue (#1976d2),
// used only within this report to signal "money / savings".
const ACCENT = '#146C43';
const ACCENT_BG = '#E9F3EC';

function formatMoney(value) {
    if (value === undefined || value === null) return '—';
    return `$${Number(value).toFixed(2)}`;
}

function SectionCard({ title, subtitle, children, sx }) {
    return (
        <Card
            sx={{
                border: '1px solid #E2E8F0',
                borderRadius: '16px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.03)',
                backgroundColor: '#FFFFFF',
                p: 3,
                ...sx,
            }}
        >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A', fontSize: '15px' }}>
                {title}
            </Typography>
            {subtitle && (
                <Typography variant="body2" sx={{ color: '#64748B', fontSize: '13px', mb: 1.75 }}>
                    {subtitle}
                </Typography>
            )}
            {children}
        </Card>
    );
}

function Bar({ pct, color }) {
    return (
        <Box sx={{ background: '#EEEDE8', borderRadius: '6px', height: '8px', overflow: 'hidden' }}>
            <Box sx={{ width: `${pct || 0}%`, height: '100%', background: color, borderRadius: '6px' }} />
        </Box>
    );
}

function RankedBarList({ rows, emptyMessage, renderLabel, renderSubline }) {
    if (!rows || rows.length === 0) {
        return (
            <Typography variant="body2" sx={{ color: '#94A3B8', fontSize: '13px' }}>
                {emptyMessage}
            </Typography>
        );
    }
    return (
        <Stack spacing={2}>
            {rows.map((row, i) => (
                <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A', fontSize: '14px' }}>
                            {renderLabel(row)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: ACCENT, fontSize: '14px' }}>
                            {formatMoney(row.savings)}
                        </Typography>
                    </Box>
                    <Bar pct={row.bar_pct} color={ACCENT} />
                    <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '12px' }}>
                        {renderSubline(row)}
                    </Typography>
                </Box>
            ))}
        </Stack>
    );
}

const ShipFromSavingsPage = () => {
    const dispatch = useDispatch();
    const storesData = useSelector((state) => state.shipFromSavings && state.shipFromSavings.storesData);
    const summary = useSelector((state) => state.shipFromSavings && state.shipFromSavings.summary);
    const underused = useSelector((state) => state.shipFromSavings && state.shipFromSavings.underused);
    const routes = useSelector((state) => state.shipFromSavings && state.shipFromSavings.routes);
    const biggestWins = useSelector((state) => state.shipFromSavings && state.shipFromSavings.biggestWins);
    const recentFlagged = useSelector((state) => state.shipFromSavings && state.shipFromSavings.recentFlagged);
    const [flaggedDrawerVisible, setFlaggedDrawerVisible] = useState(false);
    const [storeId, setStoreId] = useState('');
    const [orderSearchInput, setOrderSearchInput] = useState('');
    const [orderSearch, setOrderSearch] = useState('');

    useEffect(() => {
        dispatch(fetchDistinctStores());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchAllShipFromSavings(storeId || undefined));
    }, [dispatch, storeId]);

    // Debounce the order-id search box before refetching just the feed.
    useEffect(() => {
        const handle = setTimeout(() => {
            setOrderSearch(orderSearchInput.trim());
        }, 350);
        return () => clearTimeout(handle);
    }, [orderSearchInput]);

    useEffect(() => {
        dispatch(fetchRecentFlagged(storeId || undefined, 5, orderSearch || undefined));
    }, [dispatch, storeId, orderSearch]);

    const summaryData = (summary && summary.data) || {};
    const isLoading = !!(summary && summary.loading);

    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box', p: { xs: 2, md: 4 }, bgcolor: '#FAFAFA', minHeight: '100vh' }}>

            {/* Header, matching the Pickup/Manifests page pattern */}
            <Card
                sx={{
                    mb: 3,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '16px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.03)',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ p: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                        {/* Flex container to position "Savings" title and BETA badge side-by-side */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '20px', m: 0, letterSpacing: '-0.5px' }}>
                                Savings
                            </Typography>

                            {/* Bright Orange Pill BETA Badge */}
                            <Chip
                                label="BETA USE ONLY"
                                size="small"
                                sx={{
                                    bgcolor: '#FF9E43',
                                    color: '#FFFFFF',
                                    fontWeight: 800,
                                    fontSize: '10px',
                                    height: '20px',
                                    borderRadius: '50px',
                                    px: 0.5,
                                    letterSpacing: '0.5px',
                                    border: 'none'
                                }}
                            />
                        </Box>

                        <Typography variant="body2" sx={{ color: '#64748B', fontSize: '13.5px', mt: 0.5 }}>
                            Every new order is priced from each stocked location and compared against where it was assigned.
                        </Typography>
                    </Box>

                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="store-filter-label">Store</InputLabel>
                        <Select
                            labelId="store-filter-label"
                            label="Store"
                            value={storeId}
                            onChange={(event) => setStoreId(event.target.value)}
                            sx={{ backgroundColor: '#FFFFFF', borderRadius: '8px', fontSize: '13px' }}
                        >
                            <MenuItem value="">All stores</MenuItem>
                            {storesData && storesData.data && storesData.data.map((store) => (
                                <MenuItem key={store.store_id} value={store.store_id}>
                                    {store.store_name || store.store_id}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                {isLoading && <LinearProgress sx={{ height: '3px' }} />}
            </Card>

            {/* Hero metric */}
            <Card
                sx={{
                    mb: 3,
                    border: '1px solid #E2E8F0',
                    borderRadius: '16px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.03)',
                    backgroundColor: '#FFFFFF',
                    p: { xs: 3, sm: 4.5 },
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Left on the table
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '40px', sm: '56px' }, fontWeight: 800, color: ACCENT, fontFamily: 'monospace', lineHeight: 1, mt: 1 }}>
                        {formatMoney(summaryData.total_savings)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B', fontSize: '14px', mt: 0.5 }}>
                        Across {summaryData.groups_would_move ?? 0} of {summaryData.groups_checked ?? 0} orders
                        {' '}({summaryData.pct_would_move ?? 0}%), averaging {formatMoney(summaryData.avg_saving_per_move)} per order
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: ACCENT, fontWeight: 700, fontSize: '13px', flexShrink: 0 }}>
                    <TrendingUpIcon sx={{ fontSize: '18px' }} />
                    Trending up
                </Box>
            </Card>

            {/* Underused locations + Rebalancing routes */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} sx={{ mb: 2.5 }}>
                <Box sx={{ flex: 1 }}>
                    <SectionCard
                        title="Underused locations"
                        subtitle="Would have absorbed more shipments at a lower cost"
                    >
                        <RankedBarList
                            rows={underused && underused.data}
                            emptyMessage="No savings opportunities found."
                            renderLabel={(row) => row.location_name}
                            renderSubline={(row) => `${row.shipments} shipments would have moved in`}
                        />
                    </SectionCard>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <SectionCard
                        title="Rebalancing routes"
                        subtitle="Most common origin → cheaper-alternate pairs"
                    >
                        <RankedBarList
                            rows={routes && routes.data}
                            emptyMessage="No repeated routes found yet."
                            renderLabel={(row) => (
                                <>
                                    {row.origin_location_name}
                                    <Box component="span" sx={{ color: '#94A3B8', fontWeight: 500, mx: 0.5 }}>→</Box>
                                    {row.alternate_location_name}
                                </>
                            )}
                            renderSubline={(row) => `${row.shipments} shipments on this route`}
                        />
                    </SectionCard>
                </Box>
            </Stack>

            {/* Biggest single-order wins */}
            <Box sx={{ mb: 2.5 }}>
                <SectionCard
                    title="Biggest single-order wins"
                    subtitle="The largest individual savings found, worth checking manually"
                >
                    {(!biggestWins || !biggestWins.data || biggestWins.data.length === 0) ? (
                        <Typography variant="body2" sx={{ color: '#94A3B8', fontSize: '13px' }}>
                            No flagged orders yet.
                        </Typography>
                    ) : (
                        <Stack spacing={1.5}>
                            {biggestWins.data.map((win, i) => (
                                <Box
                                    key={win.order_id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        pb: i < biggestWins.data.length - 1 ? 1.5 : 0,
                                        borderBottom: i < biggestWins.data.length - 1 ? '1px solid #F1F5F9' : 'none',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box
                                            sx={{
                                                width: 24, height: 24, borderRadius: '999px', backgroundColor: '#F8FAFC',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: '12px', fontWeight: 800, color: '#94A3B8', flexShrink: 0,
                                            }}
                                        >
                                            {i + 1}
                                        </Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A', fontSize: '14px' }}>
                                            {win.order_id}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#64748B', fontSize: '13px' }}>
                                            {win.cheapest_location_name} instead of {win.origin_location_name}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, color: ACCENT, fontSize: '15px' }}>
                                        {formatMoney(win.savings)}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </SectionCard>
            </Box>

            {/* Recent flagged orders feed */}
            <Box>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1.5, mb: 1.5, px: 0.5 }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A', fontSize: '15px' }}>
                            Flagged orders
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748B', fontSize: '13px' }}>
                            Most recent shipments where a cheaper location was found
                        </Typography>
                    </Box>

                    {/* <TextField
                        size="small"
                        placeholder="Search order ID"
                        value={orderSearchInput}
                        onChange={(event) => setOrderSearchInput(event.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: '18px', color: '#94A3B8' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            minWidth: 220,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '10px',
                                backgroundColor: '#FFFFFF',
                                fontSize: '13.5px',
                            },
                        }}
                    /> */}
                </Box>

                <Stack spacing={1.25}>
                    {(!recentFlagged || !recentFlagged.data || recentFlagged.data.length === 0) ? (
                        <Typography variant="body2" sx={{ color: '#94A3B8', fontSize: '13px', px: 0.5 }}>
                            No flagged orders yet.
                        </Typography>
                    ) : (
                        recentFlagged.data.map((row) => (
                            <Card
                                key={row.order_id}
                                sx={{
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '12px',
                                    boxShadow: 'none',
                                    p: '16px 20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: 1.5,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                                    <Box
                                        sx={{
                                            width: 34, height: 34, borderRadius: '9px', backgroundColor: ACCENT_BG,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                        }}
                                    >
                                        <Inventory2OutlinedIcon sx={{ fontSize: '17px', color: ACCENT }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A', fontSize: '14px' }}>
                                            {row.order_id}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#64748B', fontSize: '13px', display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}
                                        >
                                            <Box component="span" sx={{ fontWeight: 700, color: '#0F172A' }}>
                                                {row.origin_location_name}
                                            </Box>
                                            <Box component="span" sx={{ fontFamily: 'monospace', color: '#94A3B8', fontSize: '12.5px' }}>
                                                ${row.origin_location_rate}
                                            </Box>
                                            <Box component="span" sx={{ color: '#CBD5E1' }}>→</Box>
                                            <Box component="span" sx={{ fontWeight: 700, color: '#0F172A' }}>
                                                {row.cheapest_location_name}
                                            </Box>
                                            <Box component="span" sx={{ fontFamily: 'monospace', color: ACCENT, fontWeight: 700, fontSize: '12.5px' }}>
                                                ${row.cheapest_location_rate}
                                            </Box>
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, color: ACCENT, fontSize: '15px' }}>
                                       Saving {formatMoney(row.savings)}
                                    </Typography>
                                </Box>
                            </Card>
                        ))
                    )}

                    {recentFlagged && recentFlagged.data && recentFlagged.data.length > 0 && (
                        <Button
                            variant="outlined"
                            sx={{
                                textTransform: 'none',
                                borderRadius: '10px',
                                fontWeight: 700,
                                fontSize: '13px',
                                color: '#64748B',
                                borderStyle: 'dashed',
                                borderColor: '#D5D3CC',
                                '&:hover': { borderColor: '#94A3B8', backgroundColor: '#F8FAFC' },
                            }}
                            // Opens the paginated flagged-orders drawer (all groups, filterable by outcome)
                            onClick={() => setFlaggedDrawerVisible(true)}
                        >
                            View all {summaryData.groups_would_move ?? 0} flagged orders
                        </Button>
                    )}
                </Stack>
            </Box>

            <FlaggedOrdersDrawer
                visible={flaggedDrawerVisible}
                storeId={storeId || undefined}
                onClose={() => setFlaggedDrawerVisible(false)}
            />

        </Box>
    );
};

export default ShipFromSavingsPage;
